/**
 * Generates a SHA-256 hash digest of the input text.
 *
 * This function uses the Web Crypto API to create a secure hash of the input string.
 * The process involves:
 * 1. Encoding the text to a buffer
 * 2. Generating a SHA-256 hash
 * 3. Converting the hash to a hexadecimal string
 *
 * @async
 * @param {string} text - The input text to hash.
 * @returns {Promise<string>} A Promise that resolves to the SHA-256 hash as a hexadecimal string.
 *
 * @throws {Error} If the Web Crypto API is not available or hashing fails.
 *
 * @example
 * // Generate a SHA-256 hash of a string
 * const hash = await digestSHA256('hello world')
 * // Might return something like 'b94d27b9934d3e08a52e52d7da7dabfac484efe37a5380ee9088f7ace2efcde9'
 */
export const digestSHA256 = async (text: string) => {
  const textAsBuffer = new TextEncoder().encode(text)
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', textAsBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(item => item.toString(16).padStart(2, '0')).join('')
}
